import { Location } from "@angular/common";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslateModule } from "@ngx-translate/core";
import { Component, ViewEncapsulation, effect, inject } from "@angular/core";
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet } from "@angular/router";

import { ButtonComponent } from "src/app/shared";
import { ParentServiceCode, AuthService, TourServicesCodes } from "src/app/core";

@Component({
  selector: "app-book",
  standalone: true,
  imports: [RouterOutlet, TranslateModule, ButtonComponent],
  templateUrl: "./book.component.html",
  styleUrl: "./book.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class BookComponent {
  private router = inject(Router);
  private location = inject(Location);
  private authService = inject(AuthService);
  private activatedRoute = inject(ActivatedRoute);

  kioskMode = false;

  parentServiceCode!: ParentServiceCode;
  serviceCode!: ParentServiceCode;
  tourServicesCodes = TourServicesCodes;

  get isEventTicket() {
    return [ParentServiceCode.EVENT].includes(this.parentServiceCode);
  }

  readonly #isGuest = this.authService.isGuest;
  readonly isCompanyUser = this.authService.isCompanyUser;

  readonly discountValue = this.authService.discountValue;
  readonly isPercentageDiscount = this.authService.isPercentageDiscount;

  constructor() {
    const routerEvents = toSignal(this.router.events);

    effect(() => {
      const isNavigationStart = routerEvents() && routerEvents() instanceof NavigationStart;
      const isNavigationEnd = routerEvents() && routerEvents() instanceof NavigationEnd;
      const isServicesRoute = routerEvents() && (routerEvents() as NavigationStart).url === "/services";

      if (this.#isGuest() && isNavigationStart && isServicesRoute) {
        this.logOut();
        return;
      }

      if (isNavigationEnd) {
        this.parentServiceCode = this.activatedRoute.snapshot.routeConfig?.path as ParentServiceCode;
        this.serviceCode = this.activatedRoute.snapshot.firstChild?.routeConfig?.path as ParentServiceCode;
        this.kioskMode = this.activatedRoute.snapshot.data["kioskMode"];
      }
    });
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.

    this.parentServiceCode = this.activatedRoute.snapshot.routeConfig?.path as ParentServiceCode;
    this.serviceCode = this.activatedRoute.snapshot.firstChild?.routeConfig?.path as ParentServiceCode;
    this.kioskMode = this.activatedRoute.snapshot.data["kioskMode"];
  }

  back() {
    if (this.#isGuest()) {
      this.authService.logOut();
      this.router.navigate(["/"]);
    } else this.location.back();
  }

  logOut() {
    // Was a bare `localStorage.clear()`, which skipped `AuthService` entirely: the user signal stayed
    // set and, since T-13, this user's cached reads would have survived their own logout.
    this.authService.logOut();
    this.router.navigate(["/"]);
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.

    if (this.#isGuest() && !this.router.url.includes("/ticket/") && !this.router.url.includes("/invoice/")) {
      this.authService.logOut();
      this.router.navigate(["/"]);
    }
  }
}
